import _ from 'lodash';
import ILayoutConfigSchema, { ILayersList, ITextConfig, IViewConfigSchema } from '../baseConfigs/layoutConfigShema';
import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ConfigManager from '../managers/ConfigManager';
import ConsoleUtil from './ConsoleUtil';
import { Loader } from "pixi.js";
import Configs from '../../../src/vendorConfigs/Configs';

export enum LocaleId {
    bigwin_title = "bigwin_title",
    hugewin_title = "hugewin_title",
    masive_Win = "masive_Win",
    megawin_title = "megawin_title",
    rc_title_session_length = "rc_title_session_length",
    rc_session_tie = "rc_session_tie",
    reality_check_minutes = "reality_check_minutes",
    rc_loss_amount = "rc_loss_amount",
    rc_win_amount = "rc_win_amount",
    but_cp = "but_cp",
    but_rl = "but_rl",
    but_ah = "but_ah",
    gui_lbl_error = "gui_lbl_error",
    ui_settings_closed_text = "ui_settings_closed_text",
    ui_settings_closed_title = "ui_settings_closed_title",
    gui_incomplete_game = "gui_incomplete_game",
    insufficient_funds = "insufficient_funds",
    insufficient_funds_title = "insufficient_funds_title",
    recovery_dialog = "recovery_dialog",
    stop_autoplay_total_win_limit = "stop_autoplay_total_win_limit",
    stop_autoplay_single_win_limit = "stop_autoplay_single_win_limit",
    stop_autoplay_loss_limit = "stop_autoplay_loss_limit",
    stop_autoplay_any_win = "stop_autoplay_any_win",
    stop_autoplay_on_feature = "stop_autoplay_on_feature",
    stop_autoplay_on_big_win = "stop_autoplay_on_big_win",
    gui_lbl_autoplay_settings = "gui_lbl_autoplay_settings"
}

/**
 * Provides translations for different languages.
 */
class Locale {
    public language: string;
    private sources = [];
    private localeIgnorelist = [
        "text",
        "title",
        "clock",
        "win",
        "bigWin",
        "value",
        "anouncer",
        "version",
        "info_text",
        "autoCount"
    ];

    /**
     * Get the translation for a string with the specified identifier.
     * @param stringId
     * @returns {String}
     */
    public getString(stringId: string, patternOverrides?: string[]) {
        let translation = stringId;

        for (let i = 0; i < this.sources.length; i++) {
            let str = this.sources[i][stringId];
            if (str && str != "") {
                translation = str;
                break;
            }
        }

        if (ConfigManager.hasConfig(ConfigurationTypes.LOCAL)) {
            const stringsOverwrites = Configs.local?.overwritePatterns || [];

            if (stringsOverwrites.length > 0) {
                stringsOverwrites.forEach(({ overwriteString, pattern }) => {
                    let key = `{${overwriteString}}`;
                    let overwriteTo = pattern();
                    if (translation.indexOf(key) !== -1) {
                        translation = translation.replace(key, overwriteTo);
                    }
                })
            }
        }


        if (patternOverrides && patternOverrides.length) {
            patternOverrides.forEach((val, index) => {
                let key = "{" + index + "}";
                if (translation.indexOf(key) !== -1) {
                    translation = translation.replace(key, val);
                } else {
                    let regx = /{{[a-zA-Z0-9_]+}}/g;
                    if (translation.match(/{{[a-zA-Z0-9_]+}}/g)) {
                        translation = translation.replace(/{{[a-zA-Z0-9_]+}}/, val);
                    }
                }

            })
        }

        if (translation === stringId) {
            translation = `|${translation}|`
        }

        return translation
    }

    /**
     * Checks wheter already processed text (by using getString() method) has been translated or not.
     *
     * @param {string} text The already translated text.
     * @param {string} stringId The localization key.
     * @return {*} 
     * @memberof Locale
     */
    public isTranslated(text: string, stringId: string): boolean {
        return text !== `|${stringId}|`;
    }

    public addRandomSuffix(translation) {
        let numSymsToAdd = Math.round(Math.random() * 50);

        for (let symIdx = 0; symIdx < numSymsToAdd; symIdx++) {
            translation += "z";
        }
        return translation;
    }

    public addSource(source: any) {
        if (this.sources.indexOf(source) != -1) {
            ConsoleUtil.log(`${source} is already added to localizations array`);
        } else {
            ConsoleUtil.log("Fonts used at PSD: ", this.getUsedFonts());
            let psdIds = this.getPsdTextIds(true);
            let notUsed = new Map<string, string>();
            let used = new Map<string, string>();
            const rules = Configs.settings.rulesData;
            
            // Older iOS does not support Object.values();
            let enumValues: string[] = [];
            for (const key in LocaleId) {
                enumValues.push(LocaleId[key].toString());
            }

            let ids = Object.keys(source);
            ids.forEach(id => {
                if (!psdIds.includes(id) &&
                    !rules.find(r => id.startsWith(r.name)) &&
                    !enumValues.includes(id) &&
                    !id.startsWith("error.")) {
                        notUsed.set(id, source[id]);
                } else {
                    used.set(id, source[id]);
                }
            })

            let missing = new Map<string, string>();
            enumValues.forEach(value => {
                if (!ids.includes(value)) {
                    missing.set(value, value);
                }
            })

            this.getPsdTextIds(false).forEach(id => {
                if (!this.localeIgnorelist.includes(id) && !id.startsWith("label-") && !id.endsWith("~currency") && !rules.find(r => id.startsWith(r.name) || id.startsWith(r.sceneTextField))) {
                    if (!ids.includes(id)) {
                        missing.set(id, id);
                    }
                }
            })

            if (notUsed.size > 0 || missing.size > 0) {
                // ConsoleUtil.red("Localization keys, not referenced by psd", Object.fromEntries(notUsed));
                missing.forEach((value, key) => {
                    used.set(key, value);
                })

                // Removed Object.fromEntries(used), now using lodash
                const obj = _.fromPairs(Array.from(used));
                ConsoleUtil.orange("Localization content suggestion (copy the object below)", obj);
            } else {
                // Checking which of the values equals to the its value.
                let notSet = [];
                ids.forEach(id => {
                    if (source[id] == id) {
                        notSet.push(id);
                    }
                })

                if (notSet.length > 0) {
                    ConsoleUtil.red("The following localization key(s) are not set properly. Either remove (rename) them from/at the psd or set text for them at the localization json:", notSet);
                }
            }

            this.sources.push(source);
        }
    }

    private getPsdTextIds(includeContent: boolean): string[] {
        const mainLayoutData = Loader.shared.resources.layoutConfig.data as ILayoutConfigSchema;
        const secondaryLayoutData = Loader.shared.resources.secondaryLayoutConfig?.data as ILayoutConfigSchema;
        
        const main = this.extractTextIdsFromSchema(mainLayoutData, includeContent);
        const secondary = this.extractTextIdsFromSchema(secondaryLayoutData, includeContent);
        let textIds: Set<string> = new Set<string>();
        main.forEach(item => {
            textIds.add(item);
        })
        secondary.forEach(item => {
            textIds.add(item);
        })

        return Array.from(textIds);
    }

    private getUsedFonts(): string[] {
        const mainLayoutData = Loader.shared.resources.layoutConfig.data as ILayoutConfigSchema;
        const secondaryLayoutData = Loader.shared.resources.secondaryLayoutConfig?.data as ILayoutConfigSchema;
        let fontNames: Set<string> = new Set<string>();
        this.extractTextsFromSchema(mainLayoutData).forEach(text => {
            fontNames.add(`${text.fontFamily} [${text.fontVariant}]`);
        });

        this.extractTextsFromSchema(secondaryLayoutData).forEach(text => {
            fontNames.add(`${text.fontFamily} [${text.fontVariant}]`);
        });

        return Array.from(fontNames);
    }

    private extractTextsFromSchema(schema: ILayoutConfigSchema): ITextConfig[] {
        let texts: ITextConfig[] = [];
        if (!schema) {
            return texts;
        }

        Object.keys(schema.layers).forEach(key => {
            const layer: IViewConfigSchema = schema.layers[key];
            let groups: ILayersList = layer.groups;
            if (groups) {
                for (const groupId in groups) {
                    if (groups[groupId].text) {
                        for (const textId in groups[groupId].text) {
                            const text = groups[groupId].text[textId];
                            texts.push(text);
                        }
                    }
                }
            }
        });

        return texts;
    }
 
    private extractTextIdsFromSchema(schema: ILayoutConfigSchema, includeTextContent: boolean): string[] {
        let textIds: string[] = [];
        if (!schema) {
            return textIds;
        }

        Object.keys(schema.layers).forEach(key => {
            const layer: IViewConfigSchema = schema.layers[key];
            let groups: ILayersList = layer.groups;
            if (groups) {
                for (const groupId in groups) {
                    if (groups[groupId].text) {
                        for (const textId in groups[groupId].text) {
                            const text = groups[groupId].text[textId];
                            let id = text.id;
                            if (text.id.indexOf("~") != -1 && text.id.indexOf("~currency") == -1) {
                                id = text.id.substring(0, text.id.indexOf("~"));
                            }

                            textIds.push(id);
                            if (includeTextContent) {
                                textIds.push(text.content);
                            }
                        }
                    }
                }
            }
        });

        return textIds;
    }
}

export default new Locale();
